import React, { createContext, ReactElement, useContext } from 'react';
import { Option } from 'fp-ts/Option';
import { useCase, CriteriaCallback } from 'adapter/in/common/hooks/useCase';
import { ErrorData, DeleteData } from 'domain/types';
import Dashboard from 'domain/entity/dashboard';
import { GetDashboard } from 'use-case/dashboard/get/getDashboardUseCase';
import {
    UpdateDashboardName,
    UpdateDashboardAccess,
    UpdateDashboardPin,
    UpdateDashboardStar,
} from 'use-case/dashboard/update/updateDashboardUseCase';
import { DeleteDashboard } from 'use-case/dashboard/delete/deleteDashboardUseCase';
import { DuplicateDashboard } from 'use-case/dashboard/duplicate/duplicateDashboardUseCase';

export type DashboardErrorResult = Option<ErrorData>;
export type DashboardResult = Option<Dashboard>;
export type UpdateDashboardResult = Option<Dashboard>;
export type DashboardDeleteResult = Option<DeleteData>;
export type DashboardDuplicateResult = Option<Dashboard>;

export type DashboardGet = {
    get: CriteriaCallback;
    getIsLoading: boolean;
    getResult: DashboardResult;
    getError: DashboardErrorResult;
    getReset: () => void;
};

export type DashboardUpdateName = {
    updateName: CriteriaCallback;
    updateNameIsLoading: boolean;
    updateNameResult: UpdateDashboardResult;
    updateNameError: DashboardErrorResult;
    updateNameReset: () => void;
};

export type DashboardUpdateStar = {
    updateStar: CriteriaCallback;
    updateStarIsLoading: boolean;
    updateStarResult: UpdateDashboardResult;
    updateStarError: DashboardErrorResult;
    updateStarReset: () => void;
};

export type DashboardUpdateAccess = {
    updateAccess: CriteriaCallback;
    updateAccessIsLoading: boolean;
    updateAccessResult: UpdateDashboardResult;
    updateAccessError: DashboardErrorResult;
    updateAccessReset: () => void;
};

export type DashboardUpdatePin = {
    updatePin: CriteriaCallback;
    updatePinIsLoading: boolean;
    updatePinResult: UpdateDashboardResult;
    updatePinError: DashboardErrorResult;
    updatePinReset: () => void;
};

export type DashboardDelete = {
    delete: CriteriaCallback;
    deleteIsLoading: boolean;
    deleteResult: DashboardDeleteResult;
    deleteError: DashboardErrorResult;
    deleteReset: () => void;
};

export type DashboardDuplicate = {
    duplicate: CriteriaCallback;
    duplicateIsLoading: boolean;
    duplicateResult: DashboardDuplicateResult;
    duplicateError: DashboardErrorResult;
    duplicateReset: () => void;
};

export type DashboardState = DashboardGet &
    DashboardUpdateName &
    DashboardUpdateAccess &
    DashboardUpdatePin &
    DashboardDelete &
    DashboardDuplicate &
    DashboardUpdateStar;

export type DashboardProvider = {
    getDashboard: GetDashboard;
    updateDashboardName: UpdateDashboardName;
    updateDashboardAccess: UpdateDashboardAccess;
    updateDashboardPin: UpdateDashboardPin;
    deleteDashboard: DeleteDashboard;
    duplicateDashboard: DuplicateDashboard;
    updateDashboardStar: UpdateDashboardStar;
    children: ReactElement;
};

export const DashboardStateContext = createContext({} as DashboardState);

export const useDashboard = (): DashboardState => useContext(DashboardStateContext);

export const DashboardProvider = (props: DashboardProvider): ReactElement => {
    const {
        children,
        getDashboard,
        updateDashboardName,
        deleteDashboard,
        updateDashboardAccess,
        updateDashboardPin,
        duplicateDashboard,
        updateDashboardStar,
    } = props;

    const { isLoading: getIsLoading, data: getResult, error: getError, execute: get, reset: getReset } = useCase(getDashboard, false);
    const {
        isLoading: updateNameIsLoading,
        data: updateNameResult,
        error: updateNameError,
        execute: updateName,
        reset: updateNameReset,
    } = useCase(updateDashboardName, false);

    const {
        isLoading: updateStarIsLoading,
        data: updateStarResult,
        error: updateStarError,
        execute: updateStar,
        reset: updateStarReset,
    } = useCase(updateDashboardStar, false);

    const {
        isLoading: updateAccessIsLoading,
        data: updateAccessResult,
        error: updateAccessError,
        execute: updateAccess,
        reset: updateAccessReset,
    } = useCase(updateDashboardAccess, false);

    const {
        isLoading: updatePinIsLoading,
        data: updatePinResult,
        error: updatePinError,
        execute: updatePin,
        reset: updatePinReset,
    } = useCase(updateDashboardPin, false);

    const { isLoading: deleteIsLoading, data: deleteResult, error: deleteError, execute: remove, reset: deleteReset } = useCase(
        deleteDashboard,
        false
    );

    const {
        isLoading: duplicateIsLoading,
        data: duplicateResult,
        error: duplicateError,
        execute: duplicate,
        reset: duplicateReset,
    } = useCase(duplicateDashboard, false);

    return (
        <DashboardStateContext.Provider
            value={{
                get,
                getIsLoading,
                getResult,
                getError,
                getReset,

                updateName,
                updateNameIsLoading,
                updateNameResult,
                updateNameError,
                updateNameReset,

                updateAccess,
                updateAccessIsLoading,
                updateAccessResult,
                updateAccessError,
                updateAccessReset,

                updatePin,
                updatePinIsLoading,
                updatePinResult,
                updatePinError,
                updatePinReset,

                delete: remove,
                deleteIsLoading,
                deleteResult,
                deleteError,
                deleteReset,

                duplicateIsLoading,
                duplicateResult,
                duplicateError,
                duplicate,
                duplicateReset,

                updateStarIsLoading,
                updateStarResult,
                updateStarError,
                updateStar,
                updateStarReset,
            }}
        >
            {children}
        </DashboardStateContext.Provider>
    );
};
